import { UseFormReturn, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP,
    IDLE_STATE_TRANSLATIONS_MAP,
    ORIENTATION_TRANSLATIONS_MAP,
} from 'src/features/systems/utils/constants';

import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { systemsMessages } from 'src/features/systems/utils/messages';
import { SystemComponentFormSchemaValues } from 'src/features/systems/utils/formSchema';
import { GATEWAY_DISPLAY_MODEL, IDLE_STATE, ORIENTATION } from 'src/features/systems/utils/types';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import { useParking } from 'src/providers/ParkingProvider';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type GatewayDisplayFormFieldsProps = {
    form: UseFormReturn<SystemComponentFormSchemaValues>;
};

const GatewayDisplayFormFields = ({ form }: GatewayDisplayFormFieldsProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const displayModel = useWatch({ control: form.control, name: 'data.display_model' });

    const { data: payStations } = useListPayStations({ parkingId });

    return (
        <>
            <FormField
                control={form.control}
                name="pay_station"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...paymentsMessages.payStations} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(paymentsMessages.payStations)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {payStations?.results.map((option) => (
                                            <SelectItem key={option.id} value={option.id}>
                                                {option.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                options={Object.values(GATEWAY_DISPLAY_MODEL)}
                form={form}
                name="data.display_model"
                label={formatMessage(systemsMessages.displayModel)}
                optionTranslationMap={GATEWAY_DISPLAY_MODEL_TRANSLATIONS_MAP}
            />
            <SelectFormField<SystemComponentFormSchemaValues>
                options={Object.values(IDLE_STATE)}
                form={form}
                name="data.idle_state"
                label={formatMessage(systemsMessages.idleState)}
                optionTranslationMap={IDLE_STATE_TRANSLATIONS_MAP}
            />
            {displayModel === GATEWAY_DISPLAY_MODEL.SP && (
                <>
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_server_ip"
                        label={formatMessage(systemsMessages.displayServerIP)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_server_api_key"
                        label={formatMessage(systemsMessages.displayServerApiKey)}
                    />
                    <SelectFormField<SystemComponentFormSchemaValues>
                        options={Object.values(ORIENTATION)}
                        form={form}
                        name="data.orientation"
                        label={formatMessage(systemsMessages.orientation)}
                        optionTranslationMap={ORIENTATION_TRANSLATIONS_MAP}
                    />
                </>
            )}
            {displayModel === GATEWAY_DISPLAY_MODEL.HUIDU && (
                <>
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_server_ip"
                        label={formatMessage(systemsMessages.displayServerIP)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_width"
                        type="number"
                        label={formatMessage(systemsMessages.screenWidth)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_height"
                        type="number"
                        label={formatMessage(systemsMessages.screenHeight)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_title_font_size"
                        type="number"
                        label={formatMessage(systemsMessages.titleFontSize)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_subtitle_font_size"
                        type="number"
                        label={formatMessage(systemsMessages.subtitleFontSize)}
                    />
                    <InputFormField<SystemComponentFormSchemaValues>
                        form={form}
                        name="data.display_title_only_font_size"
                        type="number"
                        label={formatMessage(systemsMessages.titleOnlyFontSize)}
                    />
                </>
            )}
            {(displayModel === GATEWAY_DISPLAY_MODEL.NED200 || displayModel === GATEWAY_DISPLAY_MODEL.NED200C) && (
                <InputFormField<SystemComponentFormSchemaValues>
                    form={form}
                    name="data.display_server_ip"
                    label={formatMessage(systemsMessages.displayServerIP)}
                />
            )}
        </>
    );
};

export default GatewayDisplayFormFields;
